import { useEffect, useId, useState } from 'react';
import { api } from '../../api';
import { fmtDate } from '../../lib/format';
import { belegFilter, betragText, passtText, type EntwurfTeil } from './logik';
import type { BelegeWahlAntwort, BelegWahl, RechnungenWahlAntwort, RechnungWahl, ZeileSicht } from './types';

// „Beleg wählen“ und „Rechnung wählen“ im Editor einer Bankzeile. Die Auswahl lädt einmal je Zeile, solange sie zu
// buchen ist: Belege immer, offene Rechnungen nur bei Geld rein. Klappt das Laden nicht, bleibt die Liste leer (der
// verknüpfte Beleg und „Kein Beleg“ gehen trotzdem).
export function useWahl(z: ZeileSicht, offen: boolean): { belege: BelegWahl[] | null; rechnungen: RechnungWahl[] } {
  const [belege, setBelege] = useState<BelegWahl[] | null>(null);
  const [rechnungen, setRechnungen] = useState<RechnungWahl[]>([]);
  const eingang = z.betragCents > 0;
  useEffect(() => {
    if (!offen) return;
    let weg = false;
    const url = `/api/kanzlei/buchen/zeilen/${z.id}`;
    api.get<BelegeWahlAntwort>(`${url}/belege`).then(
      (r) => !weg && setBelege(r.belege),
      () => !weg && setBelege([]),
    );
    if (eingang) {
      api.get<RechnungenWahlAntwort>(`${url}/rechnungen`).then(
        (r) => !weg && setRechnungen(r.rechnungen),
        () => {},
      );
    }
    return () => {
      weg = true;
    };
  }, [z.id, offen, eingang]);
  return { belege, rechnungen };
}

const mm = (jahr: number, monat: number) => `${String(monat).padStart(2, '0')}/${jahr}`;
const ORDNER: Record<BelegWahl['ordner'], string> = { eingang: 'Eingangsrechnungen', ausgang: 'Ausgangsrechnungen' };
// ab so vielen Zeichen sucht der Server mit (beide Ordner, ganzer Zeitraum, nicht nur die ersten 60)
const SUCHE_AB = 2;
const SUCHE_LAENGE = 80;

// Suche nach dem Namen auf dem Server, kurz nach dem letzten Tastendruck; null, solange kein Ergebnis zum Text da ist
// (oder die Suche nicht ging: dann gilt der Filter der Liste)
function useSuche(zeileId: string, suche: string): { belege: BelegWahl[] | null; laedt: boolean } {
  const text = suche.trim().slice(0, SUCHE_LAENGE);
  const [ergebnis, setErgebnis] = useState<{ zeile: string; text: string; belege: BelegWahl[] | null } | null>(null);
  useEffect(() => {
    if (text.length < SUCHE_AB) return;
    let weg = false;
    const t = setTimeout(() => {
      api.get<BelegeWahlAntwort>(`/api/kanzlei/buchen/zeilen/${zeileId}/belege?suche=${encodeURIComponent(text)}`).then(
        (r) => !weg && setErgebnis({ zeile: zeileId, text, belege: r.belege }),
        () => !weg && setErgebnis({ zeile: zeileId, text, belege: null }),
      );
    }, 250);
    return () => {
      weg = true;
      clearTimeout(t);
    };
  }, [zeileId, text]);
  if (text.length < SUCHE_AB) return { belege: null, laedt: false };
  const fertig = !!ergebnis && ergebnis.zeile === zeileId && ergebnis.text === text;
  return { belege: fertig ? ergebnis.belege : null, laedt: !fertig };
}

// Beleg eines Teils: Auswahl aus den Belegen (passende zuerst, dann die übrigen der Ordner) oder „Kein Beleg“,
// daneben „ansehen“; darüber eine Suche nach dem Dateinamen (findet auch, was nicht in der Liste steht)
export function BelegFeld({
  z,
  teil,
  belege,
  vorschlag,
  disabled,
  onWahl,
  onAnsehen,
}: {
  z: ZeileSicht;
  teil: EntwurfTeil;
  belege: BelegWahl[] | null;
  vorschlag: boolean; // anders als gespeichert (Hinweis „Vorschlag“, wenn es der sicher passende Beleg ist)
  disabled: boolean;
  onWahl(b: { id: string; name: string } | null): void;
  onAnsehen(id: string): void;
}) {
  const id = useId();
  const [suche, setSuche] = useState('');
  const liste = belege ?? [];
  const gesucht = useSuche(z.id, suche);
  const gewaehlt = teil.belegId;
  const treffer = gewaehlt ? liste.find((b) => b.id === gewaehlt) : undefined;
  // gefiltert (sofort aus der Liste, dann das Ergebnis des Servers); der gewählte bleibt immer in der Liste
  const gefiltert = gesucht.belege ?? belegFilter(liste, suche);
  const gezeigt = treffer && !gefiltert.some((b) => b.id === treffer.id) ? [treffer, ...gefiltert] : gefiltert;
  const finden = (v: string) => gefiltert.find((x) => x.id === v) ?? liste.find((x) => x.id === v);
  const zeileMonat = z.buchungstag.slice(0, 7);
  const richtung: BelegWahl['ordner'] = z.betragCents < 0 ? 'eingang' : 'ausgang';
  const text = (b: BelegWahl) => {
    const monat = `${b.jahr}-${String(b.monat).padStart(2, '0')}` !== zeileMonat ? ` · ${mm(b.jahr, b.monat)}` : '';
    const ordner = b.ordner !== richtung ? ` · ${ORDNER[b.ordner]}` : '';
    const passt = passtText(b);
    return `${b.name}${monat}${ordner}${passt ? ` · ${passt}` : ''}`;
  };

  return (
    <div className="zt-bw">
      <span className="zt-wk">
        <label className="zt-l" htmlFor={id}>
          Beleg
        </label>
        {gewaehlt && (
          <button type="button" className="linkbtn" onClick={() => onAnsehen(gewaehlt)}>
            ansehen
          </button>
        )}
      </span>
      {belege && (
        <input
          type="search"
          className="zt-such"
          value={suche}
          maxLength={SUCHE_LAENGE}
          placeholder="Belege nach Namen suchen"
          aria-label="Belege nach Namen suchen"
          autoComplete="off"
          disabled={disabled}
          onChange={(e) => setSuche(e.target.value)}
          onKeyDown={(e) => {
            // Esc leert erst den Filter (danach wie überall: Änderung abbrechen)
            if (e.key === 'Escape' && suche) {
              e.preventDefault();
              e.stopPropagation();
              setSuche('');
            }
          }}
        />
      )}
      <select
        id={id}
        value={gewaehlt ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === (gewaehlt ?? '')) return;
          const b = finden(v);
          if (!v) onWahl(null);
          else if (b) onWahl({ id: b.id, name: b.name });
        }}
      >
        <option value="">Kein Beleg</option>
        {gewaehlt && !treffer && !gefiltert.some((b) => b.id === gewaehlt) && (
          <option value={gewaehlt}>{teil.belegName ?? 'verknüpfter Beleg'}</option>
        )}
        {gezeigt.map((b) => (
          <option key={b.id} value={b.id}>
            {text(b)}
          </option>
        ))}
        {!belege && <option disabled>lädt …</option>}
        {belege && !gefiltert.length && (
          <option disabled>
            {gesucht.laedt ? 'sucht …' : suche.trim() ? 'Kein Beleg mit diesem Namen' : 'Keine Belege in diesem Zeitraum, bitte nach dem Namen suchen'}
          </option>
        )}
      </select>
      {vorschlag && treffer?.eindeutig && <span className="zt-wn">Vorschlag{passtText(treffer) ? `: ${passtText(treffer)}` : ''}</span>}
    </div>
  );
}

// Rechnung eines Teils (nur Geld rein): offene Rechnungen aus dem Rechnungsprogramm, beste zuerst, oder „Keine“.
// Wählen setzt den Debitor der Rechnung als Gegenkonto (das macht ZeileDetail).
export function RechnungFeld({
  teil,
  rechnungen,
  disabled,
  onWahl,
}: {
  teil: EntwurfTeil;
  rechnungen: RechnungWahl[];
  disabled: boolean;
  onWahl(r: RechnungWahl | null): void;
}) {
  const id = useId();
  const gewaehlt = teil.rtRechnungId;
  const da = gewaehlt ? rechnungen.some((r) => r.id === gewaehlt) : false;
  return (
    <div className="zt-re">
      <label className="zt-l" htmlFor={id}>
        Rechnung aus dem Rechnungsprogramm
      </label>
      <select
        id={id}
        value={gewaehlt ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === (gewaehlt ?? '')) return;
          const r = rechnungen.find((x) => x.id === v);
          if (!v) onWahl(null);
          else if (r) onWahl(r);
        }}
      >
        <option value="">Keine Rechnung</option>
        {gewaehlt && !da && <option value={gewaehlt}>{teil.rechnungNr ?? 'gewählte Rechnung'}</option>}
        {rechnungen.map((r) => (
          <option key={r.id} value={r.id}>
            {r.nummer} · {fmtDate(r.datum)} · {r.kunde} · {betragText(r.offenCents)} € offen
          </option>
        ))}
      </select>
    </div>
  );
}
